import { useEffect, useMemo, useState } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import { useAuthContext } from 'src/auth/hooks';
import SvgColor from 'src/components/svg-color';
import { useGetEnrolledCourses } from '../../api/courses';
import { Box } from '@mui/material';
import Chip from '@mui/material/Chip';

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  // user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  // dashboard: icon('ic_dashboard'),
  dashboard: icon('dashboard_new'),
  user: icon('ic_user_me'),
  office: icon('ic_office'),
  campaigns: icon('ic_campaigns'),
  driver: icon('ic_driver'),
  vehicle: icon('ic_vehicle'),
  region: icon('ic_region'),
  role: icon('ic_role'),
  report: icon('ic_report'),
  revzones_management: icon('ic_revzones_management'),
  performanceReport: icon('ic_performance_report'),
  appointments: icon('ic_calendar'),
  settings: icon('ic_dashboard_settings'),
  revzonesMap: icon('ic_revZone_map'),
  my_courses: icon('LMS_new'),
  all_courses: icon('global_courses_new'),
  company_courses: icon('company_courses'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const { user } = useAuthContext();
  const {enrolledCourses, enrolledCoursesLoading} = useGetEnrolledCourses(user?.companyId ,user?._id);

  const [coursesProcessed, setCoursesProcessed] = useState(false);
  const [courseChildren, setCourseChildren] = useState([]);

  useEffect(() => {
    if (enrolledCoursesLoading) {
      setCoursesProcessed(false);
      setCourseChildren([]);
      return;
    }

    // Process courses when loading is complete and we have data
    if (!enrolledCoursesLoading && enrolledCourses?.courses) {
      const processedChildren = enrolledCourses.courses.map((course) => ({
        title: (
          <Box sx={{ display: 'flex', flexDirection: 'column' , alignItems: 'flex-start', gap: 0.5, borderBottom: '1px solid #E5E5E5' }}>
            <Chip
              label={course.completed ? 'Completed' : 'In Progress'}
              size="small"
              variant="outlined"
              color={course.completed ? 'success' : 'warning'}
              sx={{
                height: 16,
                fontSize: '0.65rem',
                '& .MuiChip-label': {
                  px: 0.75,
                  py: 0,
                  lineHeight: 1
                }
              }}
            />
            <span style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '180px',
              display: 'block'
            }}>
        {course.title}
      </span>
          </Box>
        ),
        path: `${paths.dashboard.myCourses}/${course._id}`,
      }));

      setCourseChildren(processedChildren);
      setCoursesProcessed(true);
    } else if (!enrolledCoursesLoading && !enrolledCourses?.courses) {
      setCourseChildren([]);
      setCoursesProcessed(true);
    }
  }, [enrolledCoursesLoading, enrolledCourses, paths.dashboard.myCourses]);


  const getMyCoursesChildren = () => {
    if (enrolledCoursesLoading) {
      return [{ title: 'Loading courses...', path: '', disabled: true }];
    }

    if (coursesProcessed && courseChildren.length === 0) {
      return [{ title: 'No courses enrolled', path: '', disabled: true }];
    }

    if (coursesProcessed && courseChildren.length > 0) {
      return courseChildren;
    }

    return [{ title: 'Loading...', path: '', disabled: true }];
  };

  const data = useMemo(
    () => [
      // OVERVIEW
      // ----------------------------------------------------------------------
      {
        subheader: t('overview'),
        items: [
          {
            title: t('dashboard'),
            path: paths.dashboard.root,
            icon: ICONS.dashboard,
          },
          // {
          //   title: t('Global Courses'),
          //   path: paths.dashboard.allCourses,
          //   icon: ICONS.all_courses,
          // },
          {
            title: t('Company Courses'),
            path: paths.dashboard.companyCourses,
            icon: ICONS.company_courses,
            // children: getMyCoursesChildren(),
          },
          // {
          //   title: t('My Courses'),
          //   path: paths.dashboard.myCourses,
          //   icon: ICONS.my_courses,
          // },
          {
            title: t('My Courses'),
            path: paths.dashboard.myCourses,
            icon: ICONS.my_courses,
            children: getMyCoursesChildren(),
          }
          // {
          //   title: t('Sales Rep'),
          //   path: paths.dashboard.driver,
          //   icon: ICONS.driver,
          //   roles: user?.permissions?.includes('driver') ? [user?.roleName] : [],
          // },
          // {
          //   title: t('vehicle'),
          //   path: paths.dashboard.vehicle,
          //   icon: ICONS.vehicle,
          //   roles: user?.permissions?.includes('vehicle') ? [user?.roleName] : [],
          // },
          // {
          //   title: t('sales map'),
          //   path: paths.dashboard.map,
          //   icon: ICONS.region,
          //   // roles: user?.permissions?.includes('role') ? [user?.roleName] : [],
          // },
          // {
          //   title: t('Revzones Map'),
          //   path: paths.dashboard.revzonesMap,
          //   icon: ICONS.revzonesMap,
          //   // roles: user?.permissions?.includes('canvasser') ? [user?.roleName] : [],
          // },
          // {
          //   title: t('Leads & Performance'),
          //   path: paths.dashboard.leads_and_performance,
          //   icon: ICONS.performanceReport,
          //   // roles: user?.permissions?.includes('canvasser') ? [user?.roleName] : [],
          // },
          // {
          //   title: t('CRM'),
          //   path: paths.dashboard.appointments,
          //   icon: ICONS.appointments,
          //   // roles: user?.permissions?.includes('canvasser') ? [user?.roleName] : [],
          // },
          // {
          //   title: t('Activity & Reminders'),
          //   path: paths.dashboard.activityAndReminder.root,
          //   icon: ICONS.blog,
          //   children: [
          //     { title: t('Recent Acivity'), path: paths.dashboard.activityAndReminder.recentActivity },
          //     { title: t('Go Back Reminders'), path: paths.dashboard.activityAndReminder.goBack },
          //   ]
          // },
          // {
          //   title: t('Recent Activity'),
          //   path: paths.dashboard.recentActivity,
          //   icon: ICONS.blog,
          //   // roles: user?.permissions?.includes('canvasser') ? [user?.roleName] : [],
          // },
          // {
          //   title: t('Go Back Reminders'),
          //   path: paths.dashboard.goBack,
          //   icon: ICONS.kanban,
          //   // roles: user?.permissions?.includes('canvasser') ? [user?.roleName] : [],
          // },
          // {
          //   title: t('Settings'),
          //   path: paths.dashboard.settings,
          //   icon: ICONS.settings,
          //   // roles: user?.permissions?.includes('canvasser') ? [user?.roleName] : [],
          // },
          // {
          //   title: t('app'),
          //   path: paths.dashboard.root,
          //   icon: ICONS.dashboard,
          // },
          // {
          //   title: t('ecommerce'),
          //   path: paths.dashboard.general.ecommerce,
          //   icon: ICONS.ecommerce,
          // },
          // {
          //   title: t('analytics'),
          //   path: paths.dashboard.general.analytics,
          //   icon: ICONS.analytics,
          // },
          // {
          //   title: t('banking'),
          //   path: paths.dashboard.general.banking,
          //   icon: ICONS.banking,
          // },
          // {
          //   title: t('booking'),
          //   path: paths.dashboard.general.booking,
          //   icon: ICONS.booking,
          // },
          // {
          //   title: t('file'),
          //   path: paths.dashboard.general.file,
          //   icon: ICONS.file,
          // },
        ],
      },

      // MANAGEMENT
      // ----------------------------------------------------------------------
      // {
      //   // subheader: t('management'),
      //   items: [
      //     // USER
      //     {
      //       title: t('user'),
      //       path: paths.dashboard.user.root,
      //       icon: ICONS.user,
      //       children: [
      //         { title: t('profile'), path: paths.dashboard.user.root },
      //         { title: t('cards'), path: paths.dashboard.user.cards },
      //         { title: t('list'), path: paths.dashboard.user.list },
      //         { title: t('create'), path: paths.dashboard.user.new },
      //         { title: t('edit'), path: paths.dashboard.user.demo.edit },
      //         { title: t('account'), path: paths.dashboard.user.account },
      //       ],
      //     },

      //     // PRODUCT
      //     {
      //       title: t('product'),
      //       path: paths.dashboard.product.root,
      //       icon: ICONS.product,
      //       children: [
      //         { title: t('list'), path: paths.dashboard.product.root },
      //         {
      //           title: t('details'),
      //           path: paths.dashboard.product.demo.details,
      //         },
      //         { title: t('create'), path: paths.dashboard.product.new },
      //         { title: t('edit'), path: paths.dashboard.product.demo.edit },
      //       ],
      //     },

      //     // ORDER
      //     {
      //       title: t('order'),
      //       path: paths.dashboard.order.root,
      //       icon: ICONS.order,
      //       children: [
      //         { title: t('list'), path: paths.dashboard.order.root },
      //         { title: t('details'), path: paths.dashboard.order.demo.details },
      //       ],
      //     },

      //     // INVOICE
      //     {
      //       title: t('invoice'),
      //       path: paths.dashboard.invoice.root,
      //       icon: ICONS.invoice,
      //       children: [
      //         { title: t('list'), path: paths.dashboard.invoice.root },
      //         {
      //           title: t('details'),
      //           path: paths.dashboard.invoice.demo.details,
      //         },
      //         { title: t('create'), path: paths.dashboard.invoice.new },
      //         { title: t('edit'), path: paths.dashboard.invoice.demo.edit },
      //       ],
      //     },

      //     // BLOG
      //     {
      //       title: t('blog'),
      //       path: paths.dashboard.post.root,
      //       icon: ICONS.blog,
      //       children: [
      //         { title: t('list'), path: paths.dashboard.post.root },
      //         { title: t('details'), path: paths.dashboard.post.demo.details },
      //         { title: t('create'), path: paths.dashboard.post.new },
      //         { title: t('edit'), path: paths.dashboard.post.demo.edit },
      //       ],
      //     },

      //     // JOB
      //     {
      //       title: t('job'),
      //       path: paths.dashboard.job.root,
      //       icon: ICONS.job,
      //       children: [
      //         { title: t('list'), path: paths.dashboard.job.root },
      //         { title: t('details'), path: paths.dashboard.job.demo.details },
      //         { title: t('create'), path: paths.dashboard.job.new },
      //         { title: t('edit'), path: paths.dashboard.job.demo.edit },
      //       ],
      //     },

      //     // TOUR
      //     {
      //       title: t('tour'),
      //       path: paths.dashboard.tour.root,
      //       icon: ICONS.tour,
      //       children: [
      //         { title: t('list'), path: paths.dashboard.tour.root },
      //         { title: t('details'), path: paths.dashboard.tour.demo.details },
      //         { title: t('create'), path: paths.dashboard.tour.new },
      //         { title: t('edit'), path: paths.dashboard.tour.demo.edit },
      //       ],
      //     },

      //     // FILE MANAGER
      //     {
      //       title: t('file_manager'),
      //       path: paths.dashboard.fileManager,
      //       icon: ICONS.folder,
      //     },

      //     // MAIL
      //     {
      //       title: t('mail'),
      //       path: paths.dashboard.mail,
      //       icon: ICONS.mail,
      //       info: <Label color="error">+32</Label>,
      //     },

      //     // CHAT
      //     {
      //       title: t('chat'),
      //       path: paths.dashboard.chat,
      //       icon: ICONS.chat,
      //     },

      //     // CALENDAR
      //     {
      //       title: t('calendar'),
      //       path: paths.dashboard.calendar,
      //       icon: ICONS.calendar,
      //     },

      //     // KANBAN
      //     {
      //       title: t('kanban'),
      //       path: paths.dashboard.kanban,
      //       icon: ICONS.kanban,
      //     },
      //   ],
      // },

      // // DEMO MENU STATES
      // {
      //   subheader: t(t('other_cases')),
      //   items: [
      //     {
      //       // default roles : All roles can see this entry.
      //       // roles: ['user'] Only users can see this item.
      //       // roles: ['admin'] Only admin can see this item.
      //       // roles: ['admin', 'manager'] Only admin/manager can see this item.
      //       // Reference from 'src/guards/RoleBasedGuard'.
      //       title: t('item_by_roles'),
      //       path: paths.dashboard.permission,
      //       icon: ICONS.lock,
      //       roles: ['admin', 'manager'],
      //       caption: t('only_admin_can_see_this_item'),
      //     },
      //     {
      //       title: t('menu_level'),
      //       path: '#/dashboard/menu_level',
      //       icon: ICONS.menuItem,
      //       children: [
      //         {
      //           title: t('menu_level_1a'),
      //           path: '#/dashboard/menu_level/menu_level_1a',
      //         },
      //         {
      //           title: t('menu_level_1b'),
      //           path: '#/dashboard/menu_level/menu_level_1b',
      //           children: [
      //             {
      //               title: t('menu_level_2a'),
      //               path: '#/dashboard/menu_level/menu_level_1b/menu_level_2a',
      //             },
      //             {
      //               title: t('menu_level_2b'),
      //               path: '#/dashboard/menu_level/menu_level_1b/menu_level_2b',
      //               children: [
      //                 {
      //                   title: t('menu_level_3a'),
      //                   path: '#/dashboard/menu_level/menu_level_1b/menu_level_2b/menu_level_3a',
      //                 },
      //                 {
      //                   title: t('menu_level_3b'),
      //                   path: '#/dashboard/menu_level/menu_level_1b/menu_level_2b/menu_level_3b',
      //                 },
      //               ],
      //             },
      //           ],
      //         },
      //       ],
      //     },
      //     {
      //       title: t('item_disabled'),
      //       path: '#disabled',
      //       icon: ICONS.disabled,
      //       disabled: true,
      //     },
      //     {
      //       title: t('item_label'),
      //       path: '#label',
      //       icon: ICONS.label,
      //       info: (
      //         <Label color="info" startIcon={<Iconify icon="solar:bell-bing-bold-duotone" />}>
      //           NEW
      //         </Label>
      //       ),
      //     },
      //     {
      //       title: t('item_caption'),
      //       path: '#caption',
      //       icon: ICONS.menuItem,
      //       caption:
      //         'Quisque malesuada placerat nisl. In hac habitasse platea dictumst. Cras id dui. Pellentesque commodo eros a enim. Morbi mollis tellus ac sapien.',
      //     },
      //     {
      //       title: t('item_external_link'),
      //       path: 'https://www.google.com/',
      //       icon: ICONS.external,
      //     },
      //     {
      //       title: t('blank'),
      //       path: paths.dashboard.blank,
      //       icon: ICONS.blank,
      //     },
      //   ],
      // },
    ],
    [t, user, enrolledCoursesLoading, coursesProcessed, courseChildren, paths.dashboard]
  );

  return data;
}
